import React from "react";
import { TrendingUp } from "lucide-react";
import GrowthChip from "./GrowthChip";

/**
 * Revenue-Proof overlay — surfaces REAL, self-reported monthly revenue of
 * bootstrapped products solving a similar problem (sourced from TrustMRR).
 * Renders only when there's a confident, live-earner match; otherwise nothing
 * (it's a bonus signal, not a required section — no empty-state clutter).
 */
const fmt = (v) => {
  if (!v && v !== 0) return "—";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${Math.round(v / 1_000)}k`;
  return `$${Math.round(v)}`;
};

export const RevenueProofCard = ({ proof }) => {
  if (!proof || proof.status !== "ok" || !Array.isArray(proof.comps) || proof.comps.length === 0) {
    return null;
  }
  const { summary = {}, comps } = proof;

  return (
    <div
      className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white overflow-hidden"
      data-testid="revenue-proof-card"
    >
      <div className="px-4 py-2.5 bg-emerald-600 text-white text-xs font-semibold flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5">
          <TrendingUp size={14} /> Indie-proven revenue
        </span>
        {summary.badge && (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/20 text-white text-[11px] font-bold"
            data-testid="revenue-proof-badge"
          >
            💰 {summary.badge}
          </span>
        )}
      </div>

      <div className="p-4">
        <p className="text-[12px] text-zinc-500 mb-3">
          Real bootstrapped products already making money on a similar problem.
        </p>

        <div className="space-y-2">
          {comps.map((c, i) => (
            <a
              key={`${c.trustmrr_id}-${i}`}
              href={c.profile_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-zinc-200 bg-white hover:border-emerald-300 transition-colors"
              data-testid={`revenue-proof-comp-${i}`}
            >
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-zinc-900 truncate">{c.name}</div>
                <div className="text-[11px] text-zinc-500 truncate">
                  {[c.category, c.country].filter(Boolean).join(" · ")}
                  {c.description ? ` — ${c.description}` : ""}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-[13px] font-bold text-emerald-700">{fmt(c.monthly_usd)}<span className="text-[10px] font-medium text-emerald-500">/mo</span></div>
                <GrowthChip value={c.growth_mrr_30d} className="text-[10px]" />
              </div>
            </a>
          ))}
        </div>

        <p className="text-[10px] text-zinc-400 mt-3" data-testid="revenue-proof-source">
          Self-reported revenue · via TrustMRR. Verify before relying on exact figures.
        </p>
      </div>
    </div>
  );
};

export default RevenueProofCard;
